import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';

import { useDeleteCropMutation } from '../../../../adapters/crops';

const useStyles = makeStyles((theme) => ({
	card: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		transition: 'all 0.2s ease-in-out',
		cursor: 'pointer',
		'&:hover': {
			transform: 'translateY(-2px)',
			boxShadow: theme.shadows[4],
		},
	},
	cardContent: {
		flexGrow: 1,
		paddingTop: theme.spacing(1),
	},
	metricBox: {
		padding: theme.spacing(1.5),
		borderRadius: theme.spacing(1),
		backgroundColor: theme.palette.grey[50],
		textAlign: 'center',
	},
	metricLabel: {
		fontSize: '0.75rem',
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(0.5),
	},
	metricValue: {
		fontSize: '1rem',
		fontWeight: 600,
		color: theme.palette.text.primary,
	},
	avatar: {
		width: theme.spacing(6),
		height: theme.spacing(6),
		fontSize: '1.5rem',
		fontWeight: 600,
	},
}));

const getCropColor = (familyName) => {
	const colors = [
		'#4caf50', '#2196f3', '#ff9800', '#e91e63',
		'#9c27b0', '#00bcd4', '#8bc34a', '#ff5722',
		'#3f51b5', '#cddc39', '#009688', '#ffc107'
	];
	const hash = familyName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
	return colors[hash % colors.length];
};

const formatDate = (dateString) => {
	if (!dateString) return '-';
	try {
		return format(parseISO(dateString), 'MMM dd, yyyy');
	} catch (error) {
		return dateString;
	}
};

const CropCard = ({ crop, onEdit, refetch }) => {
	const classes = useStyles();
	const { t } = useTranslation(['translation', 'crops', 'soil', 'irrigation']);
	const deleteModel = useDeleteCropMutation();
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	const cropColor = getCropColor(crop.family?.name || 'Unknown');

	const handleDeleteClick = (e) => {
		e.stopPropagation();
		setDeleteDialogOpen(true);
	};

	const handleConfirmDelete = async () => {
		await deleteModel.mutateAsync(crop.id);
		setDeleteDialogOpen(false);
		refetch();
	};

	const handleCancelDelete = () => {
		setDeleteDialogOpen(false);
	};

	const handleEdit = (e) => {
		e.stopPropagation();
		onEdit(crop);
	};

	const handleCardClick = () => {
		onEdit(crop);
	};

	return (
		<Card className={classes.card} onClick={handleCardClick}>
			<CardHeader
				avatar={
					<Avatar
						className={classes.avatar}
						sx={{ bgcolor: cropColor }}
					>
						{crop.family?.name ? t(crop.family.name, { ns: 'crops' })?.[0] : '?'}
					</Avatar>
				}
				title={
					<Box display="flex" alignItems="center" gap={1}>
						<Typography variant="h6" component="div">
							{crop.family?.name ? t(crop.family.name, { ns: 'crops' }) : 'Unknown'}
						</Typography>
					</Box>
				}
				subheader={
					<Box mt={0.5}>
						{crop.variety && (
							<Chip
								label={crop.variety}
								size="small"
								variant="outlined"
								style={{ marginRight: 8, marginBottom: 4 }}
							/>
						)}
						{crop.campaign && (
							<Chip
								label={crop.campaign}
								size="small"
								color="primary"
								variant="outlined"
								style={{ marginRight: 8, marginBottom: 4 }}
							/>
						)}
						{crop.irrigation_type && (
							<Chip
								label={t(crop.irrigation_type.name, { ns: 'irrigation' })}
								size="small"
								variant="outlined"
								color="secondary"
								style={{ marginRight: 8, marginBottom: 4 }}
							/>
						)}
						{crop.soil_type && (
							<Chip
								label={t(crop.soil_type.name, { ns: 'soil' })}
								size="small"
								variant="outlined"
								style={{ marginBottom: 4 }}
							/>
						)}
					</Box>
				}
			/>

			<CardContent className={classes.cardContent}>
				<Grid container spacing={1}>
					<Grid item xs={6}>
						<Box className={classes.metricBox}>
							<Typography className={classes.metricLabel}>
								{t('Crop.seedtime')}
							</Typography>
							<Typography className={classes.metricValue}>
								{formatDate(crop.sowing)}
							</Typography>
						</Box>
					</Grid>
					<Grid item xs={6}>
						<Box className={classes.metricBox}>
							<Typography className={classes.metricLabel}>
								{t('Crop.harvest date')}
							</Typography>
							<Typography className={classes.metricValue}>
								{formatDate(crop.closure)}
							</Typography>
						</Box>
					</Grid>
					<Grid item xs={12}>
						<Box className={classes.metricBox}>
							<Typography className={classes.metricLabel}>
								{t('Crop.average performance')}
							</Typography>
							<Typography className={classes.metricValue}>
								{crop.harvested_qty ? `${crop.harvested_qty} t/ha` : '-'}
							</Typography>
						</Box>
					</Grid>
				</Grid>
			</CardContent>

			<CardActions disableSpacing>
				<Button
					size="small"
					color="primary"
					onClick={handleEdit}
					startIcon={<EditIcon />}
				>
					{t('Edit')}
				</Button>
				<Box flexGrow={1} />
				<IconButton
					size="small"
					onClick={handleDeleteClick}
					disabled={deleteModel.isLoading}
					aria-label="delete"
					style={{ color: '#9e9e9e' }}
				>
					{deleteModel.isLoading ? (
						<CircularProgress size={20} style={{ color: '#9e9e9e' }} />
					) : (
						<DeleteIcon />
					)}
				</IconButton>
			</CardActions>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={handleCancelDelete}
				onClick={(e) => e.stopPropagation()}
				aria-labelledby="delete-crop-dialog-title"
			>
				<DialogTitle id="delete-crop-dialog-title">
					{t('Delete Crop')}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{t('Are you sure you want to delete this crop?')}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelDelete} color="default">
						{t('Cancel')}
					</Button>
					<Button
						onClick={handleConfirmDelete}
						color="secondary"
						variant="contained"
						disabled={deleteModel.isLoading}
					>
						{deleteModel.isLoading ? (
							<CircularProgress size={20} />
						) : (
							t('Delete')
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
};

export default CropCard;
